import React, { useCallback, useMemo } from 'react';
import {
  CellIcon,
  ClockIcon,
  divideString,
  MailMessageIcon,
  NetworkIcon,
  ShoppingIcon,
  Text,
} from '@common-fe/common-fe';
import styled from 'styled-components';

import regexp from '@/common/regexp';
import { Option, RMR_SHOP_LINK } from '@/modules/core/components/Menu/menu.types';
import { LinkIcon, StyledCopyIcon } from '@/modules/core/components/Menu/Sidebar/SidebarOption.styles';
import { SnackbarState } from '@/modules/core/core.types';
import { useSnackbar } from '@/modules/core/hooks';
import { OrganizationView } from '@/modules/user/user.types';
import { useCopyToClipboard } from '@/utils';
import whiteLabelingConfig, { THEMES } from '@/utils/whiteLabeling/whiteLabelingConfig';

interface Props {
  organizationProperties: OrganizationView;
}

interface IconsProps {
  text: string;
}

const CopyToClipboardIcon: React.FC<IconsProps> = ({ text }) => {
  const copyToClipboard = useCopyToClipboard(text);
  const { handleAddPermanentSnackbar } = useSnackbar();

  const handleCopyToClipboard = useCallback(() => {
    copyToClipboard();
    handleAddPermanentSnackbar({
      text: 'Email copied!',
      state: SnackbarState.positive,
      closeIcon: true,
      autoRemove: true,
    });
  }, [copyToClipboard, handleAddPermanentSnackbar]);

  return <StyledCopyIcon size="18px" onClick={handleCopyToClipboard} />;
};

const StyledLink = styled.a`
  text-decoration: none;
  color: ${({ theme }) => theme.colors.navigationTextDefault};
`;
const isRmrTheme = whiteLabelingConfig.key === THEMES.rmr;
const useHelpMenuItemList = ({ organizationProperties }: Props) => {
  const phoneNumbersOnly = `+${organizationProperties?.customerServiceNumber?.replace(regexp.NOT_NUMBERS_REGEXP, '')}`;

  const isApprizeTheme = THEMES.apprize === whiteLabelingConfig.title;

  const optionList = useMemo<Option[]>(
    () => [
      ...(isRmrTheme
        ? [
            {
              key: 'shopping',
              title: 'Shop FSA Store',
              icon: <ShoppingIcon size="22px" />,
              activeBackgroundOnHover: !isApprizeTheme,
              isNotClickable: true,
              node: (
                <StyledLink href={RMR_SHOP_LINK} target="_blank">
                  Shop FSA Store
                </StyledLink>
              ),
              additionalIcon: <LinkIcon size="16px" />,
            },
          ]
        : []),
      ...(organizationProperties?.customerServiceIsEnabled
        ? [
            ...(organizationProperties.workingHours
              ? [
                  {
                    key: 'working_hours',
                    title: organizationProperties.workingHours,
                    icon: <ClockIcon size="22px" />,
                    activeBackgroundOnHover: !isApprizeTheme,
                    isNotClickable: true,
                    textStyle: { textOverflow: 'ellipsis' },
                    hintComponent: (
                      <Text size="small" color="textOnColor">
                        {organizationProperties.workingHours}
                      </Text>
                    ),
                  },
                ]
              : []),
            ...(organizationProperties.customerServiceUrlName
              ? [
                  {
                    key: 'customer_service_url_link',
                    title: organizationProperties.customerServiceUrlName,
                    icon: <NetworkIcon size="22px" />,
                    link: organizationProperties?.customerServiceUrlLink,
                    activeBackgroundOnHover: !isApprizeTheme,
                    additionalIcon: <LinkIcon size="16px" />,
                    textStyle: { textOverflow: 'ellipsis' },
                  },
                ]
              : []),
            ...(organizationProperties.customerServiceEmail
              ? [
                  {
                    key: 'customer_service_email',
                    title: organizationProperties.customerServiceEmail,
                    node: (
                      <StyledLink
                        href={`mailto:${organizationProperties.customerServiceEmail}`}
                        target="_blank"
                      >
                        {organizationProperties.customerServiceEmail}
                      </StyledLink>
                    ),
                    icon: <MailMessageIcon size="medium" color="iconPrimary" />,
                    activeBackgroundOnHover: !isApprizeTheme,
                    additionalIcon: (
                      <CopyToClipboardIcon text={organizationProperties.customerServiceEmail} />
                    ),
                    textStyle: { textOverflow: 'ellipsis' },
                  },
                ]
              : []),
            ...(organizationProperties.customerServiceNumber
              ? [
                  {
                    key: 'home',
                    title: divideString(phoneNumbersOnly, [2, 5, 8]),
                    icon: <CellIcon size="22px" />,
                    activeBackgroundOnHover: !isApprizeTheme,
                    textStyle: { textOverflow: 'ellipsis' },
                  },
                ]
              : []),
          ]
        : []),
    ],

    [organizationProperties, phoneNumbersOnly, isApprizeTheme]
  );

  return {
    formattedList: optionList || [],
  };
};

export default useHelpMenuItemList;
